<template>
  <header class="relative">
    <div
      v-if="featureStore.processing"
      class="fixed top-0 right-0 bg-green-600 text-xs lg:text-base px-2 py-0 text-white italic z-50 shadow"
    >
      <div>Processing...</div>
    </div>

    <nav
      class="shadow-lg bg-white dark:bg-neutral-900 fixed w-full z-40 top-0 start-0 border-b border-gray-200 dark:border-gray-600"
    >
      <div class="max-w-screen-xl flex flex-wrap items-center justify-between mx-auto px-4 py-2">
        <div class="flex space-x-2 md:space-x-8 items-center">
          <router-link
            :to="{ name: 'FeatureIndex' }"
            class="flex items-center space-x-1 sm:space-x-2 rtl:space-x-reverse"
          >
            <img src="@/images/logo_128.png" class="h-8 dark:brightness-125" alt="PicEditor" />
            <span class="">
              <span
                class="self-center text-lg md:text-3xl font-bold whitespace-nowrap text-primary dark:text-primary-light"
                >PicEditor</span
              >
              <span class="text-sm md:text-2xl font-semibold text-gray-600 dark:text-gray-200">.io</span>
            </span>
          </router-link>

          <router-link
            :to="{ name: 'FeatureIndex' }"
            class="hidden md:block py-2 text-lg font-medium text-dark hover:text-primary"
          >
            <i class="fa-solid fa-house"></i>
            <span>{{ $t('header.home') }}</span>
          </router-link>

          <router-link
            :to="{ name: 'PagePricing' }"
            class="hidden md:block py-2 text-lg font-medium text-dark hover:text-primary"
          >
            <i class="fa-solid fa-money-bill"></i>
            <span>Pricing</span>
          </router-link>

          <div>
            <!-- Tool menu -->
            <button
              id="dropdownToolLink"
              data-dropdown-toggle="dropdownTool"
              data-dropdown-trigger="click"
              class="inline-flex items-center p-2 justify-center text-gray-500 rounded-lg sm:space-x-1 hover:bg-gray-100 focus:outline-hidden focus:ring-2 focus:ring-gray-200 dark:text-gray-300 dark:hover:bg-gray-700 dark:focus:ring-gray-600 cursor-pointer"
            >
              <img src="@/images/icons/menu_tool.png" alt="All image tools" class="w-10 sm:w-12" />
              <span class="font-medium hidden md:block">All tools</span>
              <i class="hidden sm:block fa-solid fa-chevron-down"></i>
            </button>

            <div
              id="dropdownTool"
              class="hidden font-normal bg-white divide-y divide-gray-100 rounded-lg shadow-sm w-[96%] md:w-[700px] dark:bg-gray-700 dark:divide-gray-600"
            >
              <ul
                class="p-2 md:p-4 text-sm text-gray-700 dark:text-gray-400 flex flex-wrap"
                aria-labelledby="dropdownLargeButton"
              >
                <li
                  v-for="feature in featureStore.features"
                  :key="feature.slug"
                  class="text-sm md:text-base w-1/2 md:w-1/3"
                >
                  <router-link
                    :to="`/${feature.slug}`"
                    class="flex space-x-2 font-medium items-center rounded-sm px-4 py-2 text-sm text-body-color hover:bg-teal-100 dark:hover:bg-teal-700 text-normal"
                  >
                    <FeatureIcon :feature="feature" class="feature-icon w-1/5 sm:w-1/4" />
                    <span>{{ feature.name }}</span>
                  </router-link>
                </li>
              </ul>
            </div>
            <!--------------------->
          </div>
        </div>

        <div class="flex md:order-1 md:space-x-0 rtl:space-x-reverse items-center flex-grow justify-end">
          <!-- User menu -->
          <button
            v-if="isLessThanSmWidth || sessionStore.isSignedIn"
            id="dropdownUserLink"
            data-dropdown-toggle="dropdownUser"
            data-dropdown-trigger="click"
            class="inline-flex items-center p-2 justify-center text-gray-500 rounded-lg sm:space-x-1 hover:bg-gray-100 focus:outline-hidden focus:ring-2 focus:ring-gray-200 dark:text-gray-400 dark:hover:bg-gray-700 dark:focus:ring-gray-600 cursor-pointer"
          >
            <img
              v-if="sessionStore.isSignedIn"
              :src="sessionStore.user.image"
              alt="User profile"
              class="w-10 sm:w-12 rounded-full"
            />
            <img v-else src="@/images/icons/menu_user.png" alt="User profile" class="w-10 sm:w-12" />
            <span v-if="sessionStore.isSignedIn" class="hidden text-heading lg:block text-left">
              {{ sessionStore.user.name }}
              <span v-if="subscriptionStore.isPaidUser" class="block text-xs text-amber-500">
                <i class="fa-solid fa-crown"></i>
                {{ subscriptionStore.subscription.plan_slug.toUpperCase() }} plan
              </span>
            </span>
            <i class="hidden sm:block fa-solid fa-chevron-down"></i>
          </button>
          <div
            id="dropdownUser"
            class="z-10 hidden font-normal bg-white divide-y divide-gray-100 rounded-lg shadow-sm w-44 dark:bg-gray-700 dark:divide-gray-600"
          >
            <ul
              v-if="sessionStore.isSignedIn"
              class="py-2 text-sm text-gray-700 dark:text-gray-400"
              aria-labelledby="dropdownLargeButton"
            >
              <li>
                <div class="break-words px-4 py-2 font-medium text-normal">
                  {{ subscriptionStore.subscription.plan_slug.toUpperCase() }} plan
                </div>
              </li>
              <li>
                <router-link
                  :to="{ name: 'MyPlan' }"
                  class="block px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 text-normal dark:hover:text-white"
                >
                  <i class="fa-solid fa-money-check-dollar w-5"></i>
                  <span>Manage plan</span>
                </router-link>
              </li>
              <li>
                <router-link
                  :to="{ name: 'MyAccount' }"
                  class="block px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 text-normal dark:hover:text-white"
                >
                  <i class="fa-solid fa-user w-5"></i>
                  <span>My account</span>
                </router-link>
              </li>
              <li>
                <router-link
                  :to="{ name: 'MySetting' }"
                  class="block px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 text-normal dark:hover:text-white"
                >
                  <i class="fa-solid fa-gear w-5"></i>
                  <span>Settings</span>
                </router-link>
              </li>
              <li>
                <router-link
                  :to="{ name: 'MyTask' }"
                  class="block px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 text-normal dark:hover:text-white"
                >
                  <i class="fa-solid fa-clock w-5"></i>
                  <span>Recent tasks</span>
                </router-link>
              </li>
              <li class="mt-2 pt-2 border-t border-solid border-gray-200">
                <button
                  class="text-left block w-full px-4 py-2 text-sm text-normal hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white"
                  @click="onSignOut"
                >
                  <i class="fa-solid fa-right-from-bracket w-5"></i>
                  <span>Sign out</span>
                </button>
              </li>
            </ul>
            <ul v-else class="py-2 text-sm text-gray-700 dark:text-gray-300" aria-labelledby="dropdownLargeButton">
              <li>
                <router-link
                  :to="{ name: 'SignIn' }"
                  class="block px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white"
                >
                  <i class="fa-solid fa-right-to-bracket w-5"></i>
                  <span>Sign in</span>
                </router-link>
              </li>
              <li>
                <router-link
                  :to="{ name: 'SignUp' }"
                  class="block px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white"
                >
                  <i class="fa-solid fa-user-plus w-5"></i>
                  <span>Sign up</span>
                </router-link>
              </li>
            </ul>
          </div>

          <div v-if="sessionStore.user" class="flex items-center gap-1 mx-2" title="Total credits">
            <img src="@/images/icons/coin.png" alt="Available credits" class="hidden lg:block w-10 sm:w-12" />
            <div id="creditTotal" class="font-bold text-base lg:text-3xl text-amber-500">
              {{ subscriptionStore.credit.total }}
            </div>
          </div>
          <!--------------------->

          <div v-if="!isLessThanSmWidth && !sessionStore.isSignedIn" class="flex space-x-4 items-center text-normal">
            <router-link :to="{ name: 'SignIn' }" class="link">
              <i class="fa-solid fa-right-to-bracket"></i>
              <span>Sign in</span>
            </router-link>
            <router-link :to="{ name: 'SignUp' }" class="btn btn--primary">
              <i class="fa-solid fa-user-plus"></i>
              <span>Sign up</span>
            </router-link>
          </div>

          <button
            data-collapse-toggle="navbar-sticky"
            type="button"
            class="inline-flex items-center p-2 justify-center text-gray-500 rounded-lg md:hidden hover:bg-gray-100 focus:outline-hidden focus:ring-2 focus:ring-gray-200 dark:text-gray-400 dark:hover:bg-gray-700 dark:focus:ring-gray-600"
            aria-controls="navbar-sticky"
            aria-expanded="false"
          >
            <span class="sr-only">Open main menu</span>
            <img src="@/images/icons/menu_bar.png" alt="Menu" class="w-10 sm:w-12" />
          </button>
        </div>

        <div id="navbar-sticky" class="md:ml-4 items-center justify-between hidden w-full md:flex md:w-auto md:order-2">
          <ul
            class="flex flex-col p-4 gap-4 md:p-0 mt-4 font-medium border border-gray-100 rounded-lg bg-gray-50 md:space-x-8 rtl:space-x-reverse md:flex-row md:mt-0 md:border-0 md:bg-white dark:bg-gray-800 md:dark:bg-gray-900 dark:border-gray-700"
          >
            <li class="block lg:hidden">
              <router-link :to="{ name: 'PagePricing' }" class="py-2 text-lg font-medium text-dark hover:text-primary">
                <i class="fa-solid fa-money-bill"></i>
                <span>Pricing</span>
              </router-link>
            </li>
            <li>
              <AppThemeSwitcher />
            </li>
          </ul>
        </div>
      </div>
    </nav>
  </header>
</template>

<script setup lang="ts">
  import { ref } from 'vue'
  import { useFeatureStore } from '$/stores/feature'
  import { useSessionStore } from '$/stores/session'
  import { useSubscriptionStore } from '$/stores/subscription'
  import { useFlowbite } from '$/composables/useFlowbite'
  import FeatureIcon from '&/FeatureIcon.vue'
  import AppThemeSwitcher from '&/AppThemeSwitcher.vue'

  useFlowbite(['collapses', 'dropdowns'])

  const featureStore = useFeatureStore()
  const sessionStore = useSessionStore()
  const subscriptionStore = useSubscriptionStore()

  const onSignOut = function () {
    sessionStore.signOut().then(() => {
      window.location.href = '/'
    })
  }

  const isLessThanSmWidth = ref(window.innerWidth < 640)
  window.addEventListener('resize', () => {
    isLessThanSmWidth.value = window.innerWidth < 640
  })
</script>
